'use client';

import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

type MobileTwoColumnCarouselProps<T> = {
  items: T[];
  // eslint-disable-next-line no-unused-vars
  renderItem: (item: T, index: number) => ReactNode;
  groupSize?: number;
  className?: string;
};

const chunkItems = <T,>(items: T[], groupSize: number) => {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += groupSize) {
    chunks.push(items.slice(index, index + groupSize));
  }

  return chunks;
};

export default function MobileTwoColumnCarousel<T>({
  items,
  renderItem,
  groupSize = 2,
  className = '',
}: MobileTwoColumnCarouselProps<T>) {
  const groupedItems = useMemo(() => chunkItems(items, groupSize), [items, groupSize]);
  const [activeIndex, setActiveIndex] = useState(0);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const updateActiveIndex = () => {
      const slides = Array.from(viewport.querySelectorAll<HTMLElement>('[data-carousel-slide]'));
      if (!slides.length) {
        return;
      }

      const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    };

    updateActiveIndex();
    viewport.addEventListener('scroll', updateActiveIndex, { passive: true });
    window.addEventListener('resize', updateActiveIndex);

    return () => {
      viewport.removeEventListener('scroll', updateActiveIndex);
      window.removeEventListener('resize', updateActiveIndex);
    };
  }, [groupedItems.length]);

  if (!groupedItems.length) {
    return null;
  }

  return (
    <div className={`w-full sm:hidden ${className}`}>
      {groupedItems.length === 1 ? (
        <div className="grid grid-cols-2 gap-3">{groupedItems[0].map((item, index) => renderItem(item, index))}</div>
      ) : (
        <div
          ref={viewportRef}
          className="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto pb-1 pr-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {groupedItems.map((group, groupIndex) => (
            <div
              key={`group-${groupIndex}`}
              data-carousel-slide
              className="w-[88%] shrink-0 snap-center"
            >
              <div className="grid grid-cols-2 gap-3">
                {group.map((item, itemIndex) =>
                  renderItem(item, groupIndex * groupSize + itemIndex)
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {groupedItems.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {groupedItems.map((_, index) => (
            <span
              key={`dot-${index}`}
              className={`h-2.5 w-2.5 rounded-full transition-all duration-200 ${
                index === activeIndex ? 'scale-110 bg-[#223A27]' : 'bg-[#d6d0c3]'
              }`}
              aria-hidden="true"
            />
          ))}
        </div>
      )}
    </div>
  );
}
